'use client';

import { type ReactNode, useState } from 'react';

import type { CheckoutLifecycleEvent } from './CheckoutLifecycleEvent';
import {
  type CheckoutLifecycleListener,
  CheckoutNotificationContext,
  type CheckoutNotificationContextValue,
} from './context';

/**
 * App-wide hub for checkout lifecycle notifications (`success` / `ended` /
 * `canceled`). A checkout runs behind a soft nav (the surface that launched it —
 * a paywalled post card, a tier-locked clip — stays mounted underneath), so the
 * launcher can't observe the purchase on its own. This provider lets any surface
 * `subscribe` to lifecycle events while the checkout layer + modal chrome `notify`
 * as the engine reports success and the buyer dismisses / cancels.
 *
 * Mounted at the ROOT layout so the notifier (the `@checkout` slot) and every
 * possible listener (a post in the bio community tree, a clip on the wall, a DM
 * bubble) share ONE registry across parallel-route slots.
 *
 * Two registries, both plain `Set`s — deliberately NOT RxJS `Subject`s: listeners
 * live in the visitor bio tree, which must stay free of the rxjs footprint. The
 * `notify` set carries the real lifecycle dispatch; the `willNotify` set is the
 * pre-dispatch a consumer joins purely to `event.preventDefault()` the checkout's
 * default close nav (see {@link CheckoutNotificationContextValue}). A lazy
 * `useState` initializer keeps both sets (and the stable closures over them) alive
 * for the app's life.
 */
export function CheckoutNotificationProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [value] = useState<CheckoutNotificationContextValue>(() => {
    const listeners = new Set<CheckoutLifecycleListener>();
    const willNotifyListeners = new Set<CheckoutLifecycleListener>();

    // Snapshot before dispatch: a listener may unsubscribe (or subscribe) during
    // its own callback, and mutating the live Set mid-iteration skips entries.
    const dispatch = (
      set: Set<CheckoutLifecycleListener>,
      event: CheckoutLifecycleEvent,
    ) => {
      for (const listener of Array.from(set)) {
        try {
          listener(event);
        } catch (error) {
          console.error('[checkout-notification] listener threw:', error);
        }
      }
    };

    const register =
      (set: Set<CheckoutLifecycleListener>) =>
      (listener: CheckoutLifecycleListener) => {
        set.add(listener);
        return () => {
          set.delete(listener);
        };
      };

    return {
      notify: event => dispatch(listeners, event),
      subscribe: register(listeners),
      willNotify: event => dispatch(willNotifyListeners, event),
      subscribeWillNotify: register(willNotifyListeners),
    };
  });

  return (
    <CheckoutNotificationContext.Provider value={value}>
      {children}
    </CheckoutNotificationContext.Provider>
  );
}
